import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

import slide1 from '@/assets/marketing/slide-1.png';
import slide2 from '@/assets/marketing/slide-2.png';
import slide3 from '@/assets/marketing/slide-3.png';
import slide4 from '@/assets/marketing/slide-4.png';
import slide5 from '@/assets/marketing/slide-5.png';
import slide6 from '@/assets/marketing/slide-6.png';
import { cn } from '@/lib/utils';

// Feature highlights shown in the right pane of every auth page. Designer's
// HTML uses Swiper; we use Embla directly (the shadcn Carousel primitive is
// shaped for card galleries, not full-bleed hero slides). ARIA roles match
// shadcn Carousel's pattern so a11y parity is preserved.
//
// Pagination dot styling lives in globals.css under .swiper-pagination /
// .swiper-pagination-bullet[-active] — class names borrowed from Swiper so
// the CSS port from style.css applies unchanged.

const slides = [
  {
    image: slide1,
    title: 'Face & Liveness',
    caption: 'Liveness check detects real faces and blocks fakes instantly.',
  },
  {
    image: slide2,
    title: 'Fast and Secure eSigning',
    caption: 'Easily sign your documents online in a few clicks',
  },
  {
    image: slide3,
    title: 'KYC Check',
    caption: 'Verifies identity of an individual or business.',
  },
  {
    image: slide4,
    title: 'Stamp',
    caption: 'Eliminates physical stamp papers and manual processes.',
  },
  {
    image: slide5,
    title: 'Manual Review',
    caption: 'Supports decision-making with human judgment.',
  },
  {
    image: slide6,
    title: 'Document Templates',
    caption: 'Ready-made templates for agreements, affidavits, leases, etc.',
  },
];

export function MarketingCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true }),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section
      className="bg-black/5 w-full overflow-hidden min-[1840px]:min-w-[653px] max-lg:hidden"
      aria-roledescription="carousel"
      aria-label="SignCare feature highlights"
    >
      <div className="h-full relative">
        <div ref={emblaRef} className="overflow-hidden h-full">
          <div className="flex h-full">
            {slides.map((slide, i) => (
              // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA carousel pattern requires role="group" on each slide; <fieldset> is for form controls, not slides.
              <div
                key={slide.title}
                role="group"
                className="min-w-0 shrink-0 grow-0 basis-full h-full"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${slides.length}`}
              >
                <div className="relative overflow-hidden h-full flex flex-col">
                  <img
                    src={slide.image}
                    alt=""
                    className="object-cover w-full h-full absolute z-0"
                  />
                  {/* Gradient overlay — matches Figma: transparent → rgba(0,0,0,0.58) */}
                  <div
                    className="absolute inset-0 z-[1]"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(0,0,0,0.58) 94%)',
                    }}
                  />
                  <div className="relative z-[2] pb-24 px-7.5 flex flex-col gap-[6px] text-white mt-auto pointer-events-none">
                    <h2 className="text-2xl font-bold leading-normal tracking-[-0.48px]">
                      {slide.title}
                    </h2>
                    <p className="text-base font-normal leading-normal">{slide.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="swiper-pagination absolute bottom-8.5 left-1/2 -translate-x-1/2 z-10">
          {slides.map((slide, i) => (
            <button
              key={slide.title}
              type="button"
              className={cn(
                'swiper-pagination-bullet',
                i === selectedIndex && 'swiper-pagination-bullet-active',
              )}
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
